<template>
    <SectionViewLayout
      title='settings.housekeeper.title_search'
      :content_state='content_state'
      content_class="list-group house-keeper"
      close_class="close"
      @actionClose="onActionClose()"
    >
        <template #actions>
          <slot v-if="messages_selection.count">
            <ActionButton
                @click="onFoldersModalOpen()"
                v-tooltip='"messages.actions.move_message"'
                icon='fas fa-folder-open'
              />
              <ActionButton
                @click="onFoldersDeleteWarning()"
                v-tooltip='"messages.actions.move_trash"'
                icon='fas fa-trash-alt'
              />
            </slot>
        </template>
        <template #pre_content v-if="messages && messages.count">
            <SectionSubHeader>
              <template #pre_content>
                <InputSelectAll v-if="messages.folder_total === messages_selection.count"
                  @selectAll="onUnselectAll()"
                  v-tooltip="'messages.actions.select_reset'"
                  :checked="true"
                />

                <InputSelectAll v-else
                  @selectAll="onSelectAll()"
                  v-tooltip="'messages.actions.select_all'"
                  :checked="false"
                />
              </template>

              <template #content>
                <span>
                  <strong>{{messages_selection.count}}</strong> de <strong>{{messages.folder_total}}</strong>
                   {{Language.say('settings.generic.housekeeper.selected_messages')}}
                </span>
              </template>
            </SectionSubHeader>
        </template>

        <template #content>
          <SmartList v-if="messages && messages.count"
            @smartListItemsSet=onSmartListItemsSet
            :smart_list_items="messages.items"
          >
            <template #elements>
              <div v-for="message in smart_list_items" :key="message.id"
                class="list-item"
                :class="{'unread': !message.flags.read, 'active': messages_selection.index[message.id]}"
              >
                  <div class="container" tabindex="0">
                      <MessagesListCheckbox
                        :message="message"
                        :messages_selection="messages_selection"
                        @messageSelect="onSelect"
                      />
                      <div class="content" @click="onSelect(message)">
                          <div class="message">
                              <div class="from">
                                {{message.sender.name || message.sender.email}}
                              </div>
                              <div class="subject">
                                {{message.subject}}
                              </div>
                          </div>
                          <div class="meta">
                              <div class="datetime">
                                {{message.date.short}}
                              </div>
                              <div class="size">
                                {{message.size}}
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
            </template>
          </SmartList>

          <ContentEmpty v-else
              text='messages.list.empty'
              icon='fas fa-inbox'
          />
        </template>

        <template #footer>
            <div class="submit-desktop align-right fixed-bottom" v-if="messages && messages.count">
              <ActionButton
                @click="onFoldersModalOpen()"
                icon="fas fa-save"
                text="messages.actions.move_message"
              />
              <ActionButton
                @click="onFoldersDeleteWarning()"
                class="main-action"
                icon="fas fa-trash-alt"
                text="messages.actions.move_trash"
              />
            </div>
        </template>
    </SectionViewLayout>

    <FoldersSelectModal v-if="folders_modal_visible === true"
      @folderSubmit="onFolderSubmit"
      @modalWindowClose="onFoldersModalClose()"
      :folders_create="true"
    />

    <ConfirmModal v-if="delete_warning_visible"
        @actionCancel='onDeleteCancel()'
        @actionConfirm="onDeleteConfirm()"
        title="settings.housekeeper.delete_warning_title"
        confirm_class="danger"
    >
        <template #modal_body>
            <h3>{{Language.say('settings.housekeeper.delete_warning_text_1', [messages_selection.count])}}</h3>
            <p>{{Language.say('settings.housekeeper.delete_warning_text_2')}}</p>
            <p>{{Language.say('settings.housekeeper.delete_warning_text_3')}}</p>
        </template>
    </ConfirmModal>
</template>

<script setup>
  import {ref, reactive}              from 'vue';
  import {defineProps, defineEmits}   from 'vue';
  import {onBeforeMount, onUnmounted} from 'vue';
  import MessagesListCheckbox         from '@/vue/Views/Messages/Partials/MessagesListCheckbox.vue';
  import FoldersSelectModal           from '@/vue/Views/Folders/Modals/FoldersSelectModal.vue';
  import ConfirmModal                 from '@/vue/Views/Webmail/Modals/ConfirmModal.vue';
  import SectionViewLayout            from '@/vue/Layouts/Sections/SectionViewLayout.vue';
  import SectionSubHeader             from '@/vue/Layouts/Sections/SectionSubHeader.vue';
  import InputSelectAll               from '@/vue/Elements/Inputs/InputSelectAll.vue';
  import ActionButton                 from '@/vue/Elements/Buttons/ActionButton.vue';
  import SmartList                    from '@/vue/Elements/Tools/SmartList.vue';
  import ContentEmpty                 from '@/vue/Elements/Content/ContentEmpty.vue';
  import Language                     from '@/js/Shared/Language/Language';
  import MessagesSelectionCollection  from '@/js/Mailboxes/Collections/MessagesSelectionCollection';
  import NotificationsController      from '@/js/Webmail/Notifications/NotificationsController';
  import ContentState                 from '@/js/Shared/State/ContentState';

  const emit = defineEmits([
    'houseKeeperSearchClose', 'houseKeeperSearchConfirm',
  ])

  const props = defineProps({
    housekeeper_options: {},
    housekeeper_controller: {},
  })

  const messages_selection     = reactive(new MessagesSelectionCollection());
  const content_state          = reactive(new ContentState());
  const messages               = ref();
  const smart_list_items       = ref();
  const folders_modal_visible  = ref(false)
  const delete_warning_visible = ref(false);

  content_state.onLoaded(() => {
    setMessages();
    selectAll();
  });

  content_state.onErrorRetry(async () => {
    await fetchVirtualFolder();
  });

  onBeforeMount(async () => {
    if (await fetchVirtualFolder() === false) {
      return;
    };
  })

  onUnmounted(() => {
    props.housekeeper_controller.deleteVirtualFolder(props.housekeeper_options);
  })

  const onActionClose = () => {
    emit('houseKeeperSearchClose');
  }

  const onSelectAll = () => {
    selectAll();
  }

  const onUnselectAll = () => {
    unselectAll();
  }

  const onSelect = (event, message) => {
    messages_selection.toggle(message.id, message)
  }

  const onFolderSubmit = async (folder) => {
    setFoldersModalVisibility(false);

    if (folder.id === props.housekeeper_options.folder.id) {
      NotificationsController.addWarning('notifications.messages_move_same_warning', [folder.name]);
      return;
    }

    setFilterParams('move', folder);
    emit('houseKeeperSearchConfirm');
  }

  const onDeleteCancel = () => {
    setDeleteWarninglVisibility(false);
  }

  const onDeleteConfirm = () => {
    setFilterParams('delete_perm');
    emit('houseKeeperSearchConfirm');
  }

  const onFoldersDeleteWarning = () => {
    setDeleteWarninglVisibility(true);
  }

  const onFoldersModalOpen = () => {
    setFoldersModalVisibility(true);
  }

  const onFoldersModalClose = () => {
    setFoldersModalVisibility(false);
  }

  const onSmartListItemsSet = (items) => {
    smart_list_items.value = items;
  }

  const fetchVirtualFolder = async () => {
    return await props.housekeeper_controller.setVirtualFolder(
      props.housekeeper_options,
      content_state
    );
  }

  const setMessages = () => {
    messages.value = props.housekeeper_controller.getVirtualFolderMessages(props.housekeeper_options);
  }

  const unselectAll = () => {
    messages_selection.reset();
  }

  const selectAll = () => {
    messages_selection.addItems(messages.value.index);
  }

  const setFilterParams = (action, folder) => {
    props.housekeeper_options.action      = action;
    props.housekeeper_options.destination = folder;
    props.housekeeper_options.messages    = messages_selection.getIndexKeys();
  }

  const setFoldersModalVisibility = (value) => {
    folders_modal_visible.value = value;
  }

  const setDeleteWarninglVisibility = (value) => {
    delete_warning_visible.value = value;
  }
</script>
